// MessageShortList - MODULE
// ***********************************************************************************************************************
/// <reference types="react-scripts" />
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Stack, Typography } from "@mui/material";
import * as React from "react";
import { IMessageListItem } from "source/models/message/messageListItem";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { ToolButton } from "ui/Scripts/source/components/framework/toolButton";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { AppUtils } from "../../../framework/appUtils";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import { IMessageShortListItem } from "../../../models/message/messageShortListItem";
import * as attachmentService from "../../../models/services/attachmentService";
import * as messageService from "../../../models/services/messageService";
import { ISessionState } from "../../../models/session/sessionState";
import { AttachmentListLine } from "source/components/attachments/AttachmentList/AttachmentListLine";
import { CardContainer } from "../../framework/CardContainer";
import { DashboardCardProgress } from "../../framework/DashboardCard/DashboardCardProgress";
import { DashboardCardTitle } from "../../framework/DashboardCard/DashboardCardTitle";
import { EllipsisTypography } from "../../framework/Typography/EllipsisTypography";
import { MessageReadReceiptDialog } from "../MessageReadReceiptDialog/MessageReadReceiptDialog";
import { List } from "source/components/framework/List/List";
import { IFrontPageMessages } from "source/models/front/frontPageMessages";
import { ReadReceiptCategory } from "ui/Scripts/source/models/enums";
import { ListStateIconUnread } from "source/components/framework/List/ListStateIconUnread";

// MessageShortList
export interface IMessageShortListProp {
    sessionState: ISessionState;
    companyId?: string;
    disableGlobalLoading?: boolean;
    frontPageMessages: IFrontPageMessages;
    onPropertiesClick?: () => void;
    getTitleSuffix: (dateRange: number) => string;
}

interface IMessageShortListState {
    isLoading: boolean;
    page: number;
    hasMore: boolean;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    items: IMessageShortListItem[];
    selectedId: string;
    readReceiptMessages: IMessageShortListItem[];
    shownReceiptMessageIds: string[];
}

export class MessageShortList extends React.Component<IMessageShortListProp, IMessageShortListState> {
    _isMounted: boolean;

    constructor(props: IMessageShortListProp) {
        super(props);
        this.state = {
            isLoading: false,
            page: 1,
            hasMore: true,
            filter: "",
            sortColumn: "date",
            sortOrderIsAsc: false,
            items: [],
            selectedId: null,
            readReceiptMessages: [],
            shownReceiptMessageIds: []
        };
    }

    searchItems = async (companyId: string, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean) => {
        const props = this.props;
        const state = this.state;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => messageService.getMessageShortListItems(companyId, !refreshList ? infiniteListPageSize : (page * infiniteListPageSize), !refreshList ? page : 1, filter, sortColumn, sortOrderIsAsc, props.frontPageMessages.dateRange),
            false, props.disableGlobalLoading, () => {
                if (!this._isMounted) return;
                this.setState({ isLoading: false });
            });
        if (!result || !this._isMounted) return;
        const items = List.getListItems(state.items, state.selectedId, [], result.items, resetItems, refreshList);
        this.setState({
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            readReceiptMessages: !props.sessionState.isSuperAdmin && items.items
                ? items.items.filter(i => i.requiresReceipt && !i.requiresReceiptIsRead && this.state.shownReceiptMessageIds.indexOf(i.id) < 0).reverse()
                : [],
            selectedId: items.selectedId
        });
    };

    refreshList = () => {
        const state = this.state;
        this.searchItems(this.props.companyId, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, true);
    };

    componentDidMount(): void {
        const state = this.state;
        this.searchItems(this.props.companyId, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false);
        this._isMounted = true;
    }

    componentDidUpdate(prevProps: IMessageShortListProp, prevState: IMessageShortListState) {
        const props = this.props;
        const state = this.state;
        if ((props.frontPageMessages?.dateRange ?? 0) === (prevProps.frontPageMessages?.dateRange ?? 0)) return;
        this.searchItems(this.props.companyId, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    }

    componentWillUnmount(): void {
        this._isMounted = false;
    }

    handleAttachmentClick = async (id: string) => {
        if (!id) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    handleMessageReadReceiptDialogClose = (id: string) => {
        if (!id) return;
        const state = this.state;
        const shownReceiptMessageIds = state.shownReceiptMessageIds.slice(0);
        shownReceiptMessageIds.push(id);
        this.setState({
            shownReceiptMessageIds,
            readReceiptMessages: state.readReceiptMessages.filter(i => i.id !== id)
        });
    };

    handleAccordionChange = async (id: string, expanded: boolean) => {
        if (!expanded || !id) return;
        //Send read receipt
        const item = this.state.items.find(i => i.id === id);
        if (!item || item.isRead) return;
        const result = await messageService.sendReadReceipt(id, item.companyId, ReadReceiptCategory.Automatic);
        if (!result) return;
        //Update state
        const items = this.state.items.slice(0);
        const index = items.findIndex(i => i.id === id);
        if (index >= 0) {
            items[index].isRead = true;
        }
        const readReceiptMessages = this.state.readReceiptMessages.slice(0);
        const readReceiptIndex = readReceiptMessages.findIndex(i => i.id === id);
        if (readReceiptIndex >= 0) {
            readReceiptMessages[readReceiptIndex].isRead = true;
        }
        this.setState({
            items,
            readReceiptMessages
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <>
                    <DashboardCardTitle
                        title={Translations.CurrentTopics + props.getTitleSuffix(props.frontPageMessages.dateRange)}
                    />
                </>
                <CardContainer>
                    {!state.isLoading && state.items.length < 1 &&
                        <Alert severity="info">{Translations.NoPublishedMessages}</Alert>
                    }
                    {state.items.length > 0 &&
                        <Stack>
                            {state.items.map((item) => (
                                <Accordion
                                    key={item.id}
                                    slotProps={{
                                        transition: { unmountOnExit: true, mountOnEnter: true }
                                    }}
                                    onChange={(_e, expanded) => this.handleAccordionChange(item.id, expanded)}
                                >
                                    <AccordionSummary
                                        sx={{ alignItems: "center", width: "100%", overflow: "hidden" }}
                                    >
                                        <Box sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            width: "100%",
                                            overflow: "hidden",
                                        }}>
                                            <AvatarImage
                                                id={item.userCode}
                                                name={item.userName}
                                                image={item.userPicture}
                                                tooltip={item.userName}
                                                marginRight="1rem"
                                            />
                                            <Typography sx={{ marginRight: "1.5rem", flexGrow: 0, minWidth: "64px" }}>{Base.utcTimeToDateStr(item.date)}</Typography>
                                            <EllipsisTypography>{item.title}</EllipsisTypography>
                                            <ListStateIconUnread unread={!item.isRead && !item.requiresReceiptIsRead} />
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box dangerouslySetInnerHTML={{ __html: item.content }}></Box>
                                        {item.attachments.length > 0 &&
                                            <Stack>
                                                {item.attachments.map((attachment) =>
                                                    <AttachmentListLine
                                                        key={attachment.id}
                                                        lateThreshold={null}
                                                        attachment={attachment}
                                                        selectedId={state.selectedId}
                                                        isReadOnly
                                                        direction="row"
                                                        onDownload={this.handleAttachmentClick}
                                                    />
                                                )}
                                            </Stack>
                                        }
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Stack>
                    }
                    <DashboardCardProgress isLoading={!!props.disableGlobalLoading && state.isLoading} />
                </CardContainer>
                {!!props.onPropertiesClick &&
                    <ToolButton
                        tooltip={Translations.Properties}
                        classes={"properties"}
                        onClick={props.onPropertiesClick}
                    />
                }
                {state.readReceiptMessages.length > 0 && state.readReceiptMessages.length > 0 &&
                    <MessageReadReceiptDialog
                        item={{
                            id: state.readReceiptMessages[0].id,
                            companyId: state.readReceiptMessages[0].companyId,
                            date: state.readReceiptMessages[0].date,
                            title: state.readReceiptMessages[0].title,
                            content: state.readReceiptMessages[0].content,
                            modifyUser: {
                                name: state.readReceiptMessages[0].userName,
                                code: state.readReceiptMessages[0].userCode,
                                picture: state.readReceiptMessages[0].userPicture,
                            } as unknown as IModifyUser,
                            modifyUserCompanyName: state.readReceiptMessages[0].companyName,
                            typeName: "",
                            typeImage: state.readReceiptMessages[0].typeImage,
                            isRead: state.readReceiptMessages[0].isRead,
                            attachments: state.readReceiptMessages[0].attachments,
                        } as unknown as IMessageListItem}
                        onClose={this.handleMessageReadReceiptDialogClose}
                    />
                }
            </>
        );
    }
}